import { X } from "@repo/icons";
import { AnimatePresence, motion } from "framer-motion";

interface FullScreenModalTitleProps {
  children: string;
}

export const FullScreenModalTitle = ({
  children,
}: FullScreenModalTitleProps): JSX.Element => {
  return <h2 className="text-xl font-semibold text-gray-800">{children}</h2>;
};

interface FullScreenModalDescriptionProps {
  children: string;
}

export const FullScreenModalDescription = ({
  children,
}: FullScreenModalDescriptionProps): JSX.Element => {
  return <p className="text-md text-gray-600">{children}</p>;
};

interface FullScreenModalHeaderProps {
  children: JSX.Element | JSX.Element[];
}
export const FullScreenModalHeader = ({
  children,
}: FullScreenModalHeaderProps): JSX.Element => {
  return <div className="px-4">{children}</div>;
};

interface FullScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const FullScreenModal = ({
  isOpen,
  onClose,
  children,
}: FullScreenModalProps): JSX.Element => {
  const modalVariants = {
    hidden: {
      opacity: 0,
      y: "100%",
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 200,
        duration: 0.3,
        delayChildren: 0.1,
        staggerChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      y: "100%",
      scale: 0.8,
      transition: {
        duration: 0.1,
        ease: "easeInOut",
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -20,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          id="full-screen-modal"
          className="bg-neutral-100"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="h-full flex flex-col"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              className="flex items-center justify-between px-4 py-4"
              variants={itemVariants}
            >
              <motion.button
                onClick={onClose}
                className="active:bg-gray-100 rounded-full transition-colors"
                whileTap={{ scale: 0.9 }}
                aria-label="Fechar modal"
              >
                <X size={28} className="text-gray-600" />
              </motion.button>
            </motion.div>

            <motion.div
              className="flex-1 flex flex-col justify-between pb-4 overflow-y-scroll scrollbar-hidden"
              variants={itemVariants}
            >
              {children}
            </motion.div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
